import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import UserSettingsLayout from '../../../components/UserSettingsLayout';
import ContentBox from '../../../components/ContentBox';

const API_URL = process.env.REACT_APP_API_URL;

function StampHistory({ userData }) {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    useEffect(() => {
        if (!userData) return;

        const load = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${API_URL}stamps/my-history?page=${page}`, {
                    credentials: 'include',
                });
                const data = await response.json();

                if (data.success) {
                    setHistory(data.history);
                    setTotalPages(data.pagination.totalPages);
                    setTotalItems(data.pagination.totalItems);
                } else {
                    toast.error('Failed to load framp history');
                }
            } catch {
                toast.error('Could not connect to server');
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [userData, page]);

    if (!userData) {
        return <Navigate to="/" replace />;
    }

    const isShopPurchase = (reason) => {
        return typeof reason === 'string' && reason.startsWith('Shop Purchase');
    };

    const formatChange = (stampsEarned, reason) => {
        if (isShopPurchase(reason)) {
            const cards = Math.abs(stampsEarned) / 10;
            const isPositive = stampsEarned > 0;
            return {
                display: isPositive ? `+${cards}` : `-${cards}`,
                label: 'cards',
                isPositive,
            };
        }
        const isPositive = stampsEarned > 0;
        return {
            display: isPositive ? `+${stampsEarned}` : `${stampsEarned}`,
            label: 'framps',
            isPositive,
        };
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).replace(',', '');
    };

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > totalPages) return;
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        const delta = 2;
        const left = page - delta;
        const right = page + delta;

        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= left && i <= right)) {
                pages.push(i);
            }
        }

        const withEllipsis = [];
        let prev = null;
        for (const p of pages) {
            if (prev && p - prev > 1) withEllipsis.push('...');
            withEllipsis.push(p);
            prev = p;
        }

        return (
            <div className="flex items-center justify-between mt-4">
                <p className="text-xs text-gray-400">
                    Page {page} of {totalPages} — {totalItems} record{totalItems !== 1 ? 's' : ''}
                </p>

                <div className="flex items-center gap-1">
                    <button
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                        className="px-3 py-1 bg-gray-200 text-gray-700 font-['Jersey_10'] uppercase text-[14px] tracking-[1px] rounded disabled:opacity-40 hover:bg-gray-300 transition-colors"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.15)' }}
                    >
                        Prev
                    </button>

                    {withEllipsis.map((p, i) =>
                        p === '...' ? (
                            <span key={`ellipsis-${i}`} className="px-2 text-gray-500 font-['Jersey_10'] text-[14px]">
                                ...
                            </span>
                        ) : (
                            <button
                                key={p}
                                onClick={() => handlePageChange(p)}
                                className={`px-3 py-1 font-['Jersey_10'] uppercase text-[14px] tracking-[1px] rounded transition-colors ${
                                    p === page
                                        ? 'bg-maroon text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.15)' }}
                            >
                                {p}
                            </button>
                        )
                    )}

                    <button
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                        className="px-3 py-1 bg-gray-200 text-gray-700 font-['Jersey_10'] uppercase text-[14px] tracking-[1px] rounded disabled:opacity-40 hover:bg-gray-300 transition-colors"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.15)' }}
                    >
                        Next
                    </button>
                </div>
            </div>
        );
    };

    return (
        <UserSettingsLayout>
            <ContentBox theme="default" tint="lightskyblue" header="Framp History">
                {loading ? (
                    <p className="text-gray-600 font-['Jersey_10'] text-[16px]">Loading...</p>
                ) : history.length === 0 ? (
                    <p className="text-gray-600 font-['Jersey_10'] text-[16px]">
                        You haven't earned any framps yet.
                    </p>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                <tr className="bg-gray-800 text-white">
                                    <th className="text-left font-['Jersey_10'] uppercase text-[15px] tracking-[1px] px-4 py-2 whitespace-nowrap">
                                        Date / Time
                                    </th>
                                    <th className="text-center font-['Jersey_10'] uppercase text-[15px] tracking-[1px] px-4 py-2 whitespace-nowrap">
                                        Change
                                    </th>
                                    <th className="text-left font-['Jersey_10'] uppercase text-[15px] tracking-[1px] px-4 py-2">
                                        Reason
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                {history.map((row, index) => {
                                    const { display, label, isPositive } = formatChange(row.stampsEarned, row.reason);
                                    const isEven = index % 2 === 0;

                                    return (
                                        <tr
                                            key={row.id}
                                            className={`border-b border-gray-200 transition-colors hover:bg-gray-100 ${
                                                isEven ? 'bg-white' : 'bg-gray-50'
                                            }`}
                                        >
                                            <td className="px-4 py-2 font-['Jersey_10'] text-[15px] text-gray-700 whitespace-nowrap">
                                                {formatDate(row.createdAt)}
                                            </td>
                                            <td className="px-4 py-2 text-center">
                                                    <span
                                                        className={`inline-flex items-center gap-1 font-['Jersey_10'] text-[15px] tracking-[1px] px-3 py-0.5 rounded ${
                                                            isPositive
                                                                ? 'bg-green-100 text-green-700'
                                                                : 'bg-red-100 text-red-700'
                                                        }`}
                                                        style={{ boxShadow: '0 1px 0 0 rgba(0,0,0,0.15)' }}
                                                    >
                                                        {display}
                                                        <span className="text-[11px] uppercase opacity-70">{label}</span>
                                                    </span>
                                            </td>
                                            <td className="px-4 py-2 font-['Jersey_10'] text-[15px] text-gray-700">
                                                {row.reason}
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>

                        {renderPagination()}
                    </>
                )}
            </ContentBox>
        </UserSettingsLayout>
    );
}

export default StampHistory;